// frontend/src/pages/PaymentConditionsPage.js
import React, { useState, useEffect } from 'react';
import { Search, Edit, Trash2, Plus, X, Banknote, Loader2, AlertCircle } from 'lucide-react';
import useApi from '../hooks/useApi';

const PaymentConditionsPage = ({ currentUser }) => {
    const { apiFetch } = useApi();
    
    // --- VERIFICAÇÃO DE PERMISSÕES GRANULARES ---
    const isSuper = currentUser?.is_super_user;
    const perms = currentUser?.permissions || {};
    const canCreate = isSuper || perms.payment_conditions_create;
    const canEdit = isSuper || perms.payment_conditions_edit;
    const canDelete = isSuper || perms.payment_conditions_delete;

    // --- ESTADOS ---
    const [conditions, setConditions] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    
    // Estados do Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCondition, setEditingCondition] = useState(null);
    const [formData, setFormData] = useState({ name: '' });
    const [isSaving, setIsSaving] = useState(false);

    // --- BUSCAR DADOS ---
    const fetchConditions = async () => {
        setIsLoading(true);
        try {
            const data = await apiFetch('/payment-conditions');
            setConditions(data || []);
        } catch (error) {
            console.error("Erro ao buscar condições de pagamento:", error);
            setConditions([
                { id: 1, name: 'À Vista' },
                { id: 2, name: '30 Dias' },
                { id: 3, name: '30 / 60 / 90 Dias' },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- FILTRO DE PESQUISA ---
    const filteredConditions = conditions.filter(c => 
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // --- AÇÕES DO MODAL ---
    const handleOpenModal = (condition = null) => {
        if (condition) {
            setEditingCondition(condition);
            setFormData({ name: condition.name });
        } else {
            setEditingCondition(null);
            setFormData({ name: '' });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingCondition(null);
        setFormData({ name: '' });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            alert("O nome da condição é obrigatório.");
            return;
        }

        setIsSaving(true);
        try {
            if (editingCondition) {
                // Atualizar existente
                await apiFetch(`/payment-conditions/${editingCondition.id}`, {
                    method: 'PUT',
                    body: { name: formData.name }
                });
            } else {
                // Criar nova
                await apiFetch('/payment-conditions', {
                    method: 'POST',
                    body: { name: formData.name }
                });
            }
            
            await fetchConditions();
            handleCloseModal();
        } catch (error) {
            console.error("Erro ao salvar:", error);
            alert("Erro ao salvar a condição de pagamento. Tente novamente.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Tem certeza que deseja excluir a condição "${name}"?`)) return;

        try {
            await apiFetch(`/payment-conditions/${id}`, { method: 'DELETE' });
            await fetchConditions();
        } catch (error) {
            console.error("Erro ao excluir:", error);
            alert("Erro ao excluir. Pode estar em uso.");
        }
    };

    return (
        <div className="p-6 max-w-6xl mx-auto animate-in fade-in duration-300">
            
            {/* CABEÇALHO */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                        <Banknote className="text-blue-600" size={28} /> Condições de Pagamento
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Gerencie as condições de pagamento para suas compras.</p>
                </div>

                {/* SÓ MOSTRA O BOTÃO NOVA CONDIÇÃO SE TIVER PERMISSÃO DE CREATE */}
                {canCreate && (
                    <button 
                        onClick={() => handleOpenModal()}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium shadow-sm transition-colors whitespace-nowrap"
                    >
                        <Plus size={18} /> Nova Condição
                    </button>
                )}
            </div>

            {/* CARD PRINCIPAL */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                
                {/* BARRA DE PESQUISA */}
                <div className="p-4 border-b border-gray-200 bg-white">
                    <div className="relative max-w-full">
                        <input 
                            type="text" 
                            placeholder="Pesquisar condição..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm transition-all"
                        />
                        <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                    </div>
                </div>

                {/* TABELA DE RESULTADOS */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-[11px] border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4">NOME DA CONDIÇÃO</th>
                                {(canEdit || canDelete) && <th className="px-6 py-4 text-right w-32">AÇÕES</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={(canEdit || canDelete) ? 2 : 1} className="px-6 py-12 text-center">
                                        <Loader2 size={32} className="animate-spin text-blue-600 mx-auto" />
                                    </td>
                                </tr>
                            ) : filteredConditions.length === 0 ? (
                                <tr>
                                    <td colSpan={(canEdit || canDelete) ? 2 : 1} className="px-6 py-12 text-center text-gray-500 flex flex-col items-center">
                                        <AlertCircle size={32} className="text-gray-300 mb-2" />
                                        <p>Nenhuma condição de pagamento encontrada.</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredConditions.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-gray-800">{item.name}</td>
                                        {(canEdit || canDelete) && (
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-4">
                                                    {canEdit && (
                                                        <button 
                                                            onClick={() => handleOpenModal(item)}
                                                            className="text-blue-600 hover:text-blue-800 transition-colors"
                                                            title="Editar"
                                                        >
                                                            <Edit size={18} />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button 
                                                            onClick={() => handleDelete(item.id, item.name)}
                                                            className="text-red-500 hover:text-red-700 transition-colors"
                                                            title="Excluir"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL DE CADASTRO/EDIÇÃO */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50">
                            <h3 className="font-bold text-gray-800 text-lg">
                                {editingCondition ? 'Editar Condição' : 'Nova Condição'}
                            </h3>
                            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-1 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSave} className="p-6">
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Nome da Condição <span className="text-red-500">*</span></label>
                                <input 
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Ex: 30 / 60 / 90 Dias"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                <button 
                                    type="button" 
                                    onClick={handleCloseModal}
                                    className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors disabled:bg-blue-400"
                                >
                                    {isSaving ? <Loader2 size={16} className="animate-spin" /> : null}
                                    {isSaving ? 'Salvando...' : 'Salvar Condição'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentConditionsPage;